import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { tokenManager } from '@/shared/utils/tokenManager';
import { safeString } from '@/lib/utils';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8005';

export function useChat() {
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const abortRef = useRef(null);

  const sendMessage = useCallback(async (text, existingThreadId = null) => {
    const aipToken = tokenManager.getAipToken();
    const moduleTokens = tokenManager.getAllModuleTokens();
    const tid = existingThreadId || threadId;

    // Add user message
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setIsStreaming(true);
    setCurrentForm(null);

    // Prepare for streaming AI response
    let aiContent = '';
    setMessages((prev) => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await fetch(`${API_BASE}/embed/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(aipToken ? { Authorization: `Bearer ${aipToken}` } : {}),
        },
        body: JSON.stringify({
          thread_id: tid,
          message: text,
          module_tokens: moduleTokens,
        }),
        signal: controller.signal,
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          let data;
          try {
            data = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          if (data.type === 'token') {
            aiContent += data.content;
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: aiContent,
                isStreaming: true,
              };
              return updated;
            });
          } else if (data.type === 'interrupt') {
            // Form interrupt — show form to user
            if (data.thread_id) setThreadId(data.thread_id);
            setCurrentForm(data);
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: aiContent || data.explanation || '',
                type: 'form',
                formData: data,
              };
              return updated;
            });
          } else if (data.type === 'step_result') {
            setMessages((prev) => [
              ...prev,
              { role: 'system', type: 'step_result', content: data.results },
            ]);
          } else if (data.type === 'done') {
            if (data.thread_id) setThreadId(data.thread_id);
          } else if (data.type === 'error') {
            setMessages((prev) => [
              ...prev,
              { role: 'system', type: 'error', content: safeString(data.message, 'An error occurred') },
            ]);
          }
        }
      }

      // Finalize — clear isStreaming on ALL messages, not just the last one
      setMessages((prev) =>
        prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg))
      );
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error(err.message || 'Failed to send message');
        setMessages((prev) => [
          ...prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)),
          { role: 'system', type: 'error', content: err.message },
        ]);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [threadId]);

  const submitForm = useCallback(async (formData) => {
    const aipToken = tokenManager.getAipToken();

    setCurrentForm(null);
    setIsStreaming(true);

    let aiContent = '';
    setMessages((prev) => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await fetch(`${API_BASE}/embed/v1/chat/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(aipToken ? { Authorization: `Bearer ${aipToken}` } : {}),
        },
        body: JSON.stringify({
          thread_id: threadId,
          form_data: formData,
        }),
        signal: controller.signal,
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          let data;
          try {
            data = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          if (data.type === 'token') {
            aiContent += data.content;
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: aiContent,
                isStreaming: true,
              };
              return updated;
            });
          } else if (data.type === 'interrupt') {
            if (data.thread_id) setThreadId(data.thread_id);
            setCurrentForm(data);
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: aiContent || data.explanation || '',
                type: 'form',
                formData: data,
              };
              return updated;
            });
          } else if (data.type === 'step_result') {
            setMessages((prev) => [
              ...prev,
              { role: 'system', type: 'step_result', content: data.results },
            ]);
          } else if (data.type === 'done') {
            // Done
          } else if (data.type === 'error') {
            setMessages((prev) => [
              ...prev,
              { role: 'system', type: 'error', content: safeString(data.message, 'An error occurred') },
            ]);
          }
        }
      }

      // Finalize — clear isStreaming on ALL messages, not just the last one
      setMessages((prev) =>
        prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg))
      );
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error(err.message || 'Connection lost');
        setMessages((prev) => [
          ...prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)),
          { role: 'system', type: 'error', content: err.message },
        ]);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [threadId]);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  const resetChat = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    setMessages([]);
    setThreadId(null);
    setCurrentForm(null);
    setIsStreaming(false);
  }, []);

  const loadMessages = useCallback(async (tid) => {
    const aipToken = tokenManager.getAipToken();
    if (!aipToken) return;

    setIsLoadingHistory(true);
    try {
      const res = await fetch(`${API_BASE}/embed/v1/conversations/${tid}/messages`, {
        headers: { Authorization: `Bearer ${aipToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setThreadId(tid);
        setMessages(data.messages || []);
        setCurrentForm(null);
      }
    } catch {
      // Silently fail — user can retry by navigating again
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  return {
    messages,
    isStreaming,
    isLoadingHistory,
    currentForm,
    threadId,
    sendMessage,
    submitForm,
    stopStreaming,
    resetChat,
    loadMessages,
    setMessages,
    setThreadId,
  };
}
